import {
  Scene,
  Vector3,
  AmbientLight,
  BufferGeometry,
  BufferAttribute,
  MathUtils
} from "three";
import { setViewerTooltip, clearViewerTooltip } from "../../viewer";
import { CustomMouseEvent } from "../controls";
import GPUPicker from "../gpu-picker";
import { TagMaterial } from "../rendering";
import { TagsHandler, TagMesh, TagItem, TagFile } from ".";

export class SceneViewTags {
  public scene: Scene;
  private tagsHandler: TagsHandler;

  private numVertices = 6;
  private uvs: number[] = [0, 1, 0, 0, 1, 1, 0, 0, 1, 0, 1, 1];
  private corners: number[] = [0, 2, 1, 2, 3, 1];
  private vertices: Vector3[] = [
    new Vector3(-0.5, 0.5, 0),
    new Vector3(0.5, 0.5, 0),
    new Vector3(-0.5, -0.5, 0),
    new Vector3(0.5, -0.5, 0)
  ]

  private pickingMeshes: TagMesh[] = [];
  private standardMeshes: TagMesh[] = [];
  private maxDistance = 0;
  private flashTimeout: NodeJS.Timeout;

  constructor(tagsHandler: TagsHandler) {
    this.tagsHandler = tagsHandler;

    this.initScene();
  }

  get viewer() {
    return this.tagsHandler.viewer;
  }

  get meshes() {
    return [...this.pickingMeshes, ...this.standardMeshes];
  }

  get materials() {
    return this.meshes.map(mesh => mesh.material);
  }

  get numberOfTags() {
    let count = 0;

    this.standardMeshes.forEach(mesh => {
      let geometry = mesh.geometry;
      let positions = geometry.attributes.position;
      count += (positions.count / this.numVertices);
    });

    return count;
  }

  private get picker(): GPUPicker {
    return this.viewer.gpuPickers.tags;
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  reconcile(files: TagFile[]) {
    this.scene.clear();
    this.picker.clear();

    this.standardMeshes = [];
    this.pickingMeshes = [];

    files.forEach(file => {
      if (!file.visible) return;

      const sceneViewTags = file.sceneViewTags;
      if (sceneViewTags.length === 0) return;

      const size = sceneViewTags.length * this.numVertices;
      const positions = new Float32Array(size * 3);
      const selected = new Float32Array(size);
      const hovering = new Float32Array(size);
      const gpuIndices = new Int32Array(size);
      const uvs = new Float32Array(size * 2);

      sceneViewTags.forEach((tag, i) => {
        const coordinate = tag.position.scene;

        this.corners.forEach((corner, cornerNumber) => {
          const tagIndex = ((this.corners.length * i) + cornerNumber) * 3;
          const points = this.vertices[corner];

          positions[tagIndex] = points.x + coordinate.x;
          positions[tagIndex + 1] = points.y + coordinate.y;
          positions[tagIndex + 2] = points.z + coordinate.z;
        });

        this.uvs.forEach((uv, j) => {
          uvs[(this.uvs.length * i) + j] = uv;
        });
      });

      const geometry = new BufferGeometry();

      geometry.setAttribute('position', new BufferAttribute(positions, 3));
      geometry.setAttribute('selected', new BufferAttribute(selected, 1));
      geometry.setAttribute('hovering', new BufferAttribute(hovering, 1));
      geometry.setAttribute('gpu_index', new BufferAttribute(gpuIndices, 1));
      geometry.setAttribute('uv', new BufferAttribute(uvs, 2));

      const tagMaterial = new TagMaterial(file.texture, file.size, false);
      const standardMesh = new TagMesh(geometry, tagMaterial, sceneViewTags);
      this.scene.add(standardMesh);

      const pickingMaterial = new TagMaterial(file.texture, file.size, true);
      const pickingMesh = new TagMesh(geometry, pickingMaterial, sceneViewTags);
      this.picker.add(pickingMesh);

      this.standardMeshes.push(standardMesh);
      this.pickingMeshes.push(pickingMesh);
    });

    this.updateUniforms();
    this.updatePicker();
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  setMaxDistance(value: number) {
    this.maxDistance = value;

    this.updateUniforms();
    this.updatePicker();
  };

  public updateUniforms() {
    if (this.numberOfTags === 0) return;

    const camera = this.viewer.camera;
    if (!camera) return;

    const position = camera.position;
    const rotation = camera.rotation.toVector3();
    const cameraLoading = this.viewer.initialCameraLoading;

    const controls = this.viewer.controls;
    const target = controls.getCameraTarget();

    // Set mesh visibility
    this.meshes.forEach(mesh => {
      mesh.visible = !cameraLoading;
    });

    this.materials.forEach((material: TagMaterial) => {
      material.currentCameraRotation = rotation;
      material.currentCameraPosition = position;
      material.currentCameraTarget = target;
      material.windowHeight = window.innerHeight;
      material.fov = MathUtils.degToRad(camera.fov);
      material.distance = this.maxDistance;
      material.timeStamp = performance.now() % 1000;
      material.needsUpdate = true;
    });
  }

  tagFromPickerIndex(mesh: TagMesh, index: number) {
    if (!mesh) return;

    const attributes = mesh.geometry.attributes;
    const gpuIndex = attributes.gpu_index.array as Array<number>;
    const arrayIndex = gpuIndex.indexOf(index);
    const tagIndex = Math.floor(arrayIndex / this.numVertices);

    return mesh.tags[tagIndex] as TagItem;
  }

  getPickerResults(event): TagItem {
    this.picker.update();

    const value = this.picker.value(event);
    const mesh = this.picker.getSelectedObject(value) as TagMesh;
    const tag = this.tagFromPickerIndex(mesh, value);

    if (tag) {
      this.setHovering(tag.id);
    } else {
      this.setHovering(null);
    }

    return tag;
  }

  setHovering(tagID: string) {
    this.meshes.forEach(mesh => {
      const attributes = mesh.geometry.attributes;
      const hovering = attributes.hovering.array as Array<number>;
      const index = mesh.tags.map(tag => tag.id).indexOf(tagID);
      hovering.fill(0.0);

      const baseIndex = index * this.numVertices;
      for (let i = 0; i < this.numVertices; i++) {
        if (baseIndex < 0) continue;
        hovering[baseIndex + i] = 1.0;
      }

      attributes.hovering.needsUpdate = true;
    });
  }

  setSelected(tagID: string, timeoutSeconds: number) {
    clearTimeout(this.flashTimeout);

    this.meshes.forEach(mesh => {
      const attributes = mesh.geometry.attributes;
      const selected = attributes.selected.array as Array<number>;
      const index = mesh.tags.map(tag => tag.id).indexOf(tagID);
      selected.fill(0.0);

      const baseIndex = index * this.numVertices;
      for (let i = 0; i < this.numVertices; i++) {
        if (baseIndex < 0) continue;
        selected[baseIndex + i] = 1.0;
      }

      attributes.selected.needsUpdate = true;
    });

    if (!tagID) return;

    this.flashTimeout = setTimeout(() => {
      this.setSelected(null, timeoutSeconds);
    }, timeoutSeconds * 1000);
  }

  onMouseUp(event: CustomMouseEvent) {
    if (event.mouseMoved) return false;

    if (event.isLeftClick) {
      const tag = this.getPickerResults(event);

      if (tag) {
        const selectedTagID = tag.id;
        this.viewer.showTagDetails(selectedTagID);
      }
    }

    return false;
  }

  onMouseMove(event: CustomMouseEvent) {
    if (event.mouseMoved) return;
    if (this.numberOfTags === 0) return;

    const tag = this.getPickerResults(event);

    if (tag) {
      const selectedTagID = tag.id;
      let tooltipText = this.tagsHandler
        .getTooltipText(selectedTagID);

      setViewerTooltip(tooltipText);
    } else {
      clearViewerTooltip();
    }
  }

  update() {
    this.updateUniforms();
  }
}
